import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ONBOARDING_STEPS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useProjectContext } from '@/project/ProjectContext';
import styles from './OnboardingChecklist.module.css';

export function OnboardingChecklist() {
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const projectId = selectedProjectId;

  const q = useQuery({
    queryKey: ['onboarding', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        steps: { id: string; label: string; hint: string; done: boolean }[];
        all_done: boolean;
        hidden: boolean;
      }>(`/api/platform/onboarding?project_id=${projectId}`),
  });

  const hide = useMutation({
    mutationFn: () =>
      apiFetch('/api/platform/ui-state', {
        method: 'PATCH',
        body: JSON.stringify({ hide_onboarding: projectId }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['onboarding', projectId] }),
  });

  if (!projectId || !q.data || q.data.hidden || q.data.all_done) return null;

  const doneCount = q.data.steps.filter((s) => s.done).length;
  const total = q.data.steps.length;

  return (
    <section className={styles.card} aria-labelledby="onboarding-title">
      <div className={styles.head}>
        <h2 id="onboarding-title" className={styles.title}>
          Aan de slag
          <span className={styles.progress}>
            {' '}
            · {doneCount} van {total} klaar
          </span>
        </h2>
        <Button variant="ghost" onClick={() => hide.mutate()} disabled={hide.isPending}>
          Verberg
        </Button>
      </div>
      <ul className={styles.list}>
        {q.data.steps.map((s) => (
          <li key={s.id} className={s.done ? styles.done : ''}>
            <span>{s.done ? '✓' : '○'}</span>
            <div>
              <strong>{s.label}</strong>
              <div className={styles.hint}>{s.hint}</div>
            </div>
          </li>
        ))}
      </ul>
      <p className={styles.links}>
        {ONBOARDING_STEPS.filter((s) => s.route).map((s, i) => (
          <span key={s.id}>
            {i > 0 ? ' · ' : null}
            <Link to={s.route!}>{s.label.split(' ')[0]}</Link>
          </span>
        ))}
      </p>
    </section>
  );
}
